(function () {
  'use strict';

  var compareOwnerSeq = 0;

  // ---------------------------------------------------------------------------
  // SACompareEngine
  // Web Audio engine for the Compare Basses modal.
  // Supports sample-locked A/B switching across 11 basses × 3 songs × 3 mixes.
  // ---------------------------------------------------------------------------

  function SACompareEngine(config) {
    // Config
    this._songs    = config.songs    || [];
    this._mixes    = config.mixes    || [];
    this._basses   = config.basses   || [];
    this._urlPattern = config.urlPattern || '';

    // Playback state
    this._song    = null;
    this._mix     = null;
    this._bass    = null;
    this._playing = false;

    // Position memory: { songId: seconds }
    this._songPositions = {};

    // AudioContext + source node
    this._ctx    = null;
    this._source = null;
    this._startTime    = 0;  // ctx.currentTime when playback started
    this._startOffset  = 0;  // buffer offset at that moment

    // Buffer cache: key → AudioBuffer
    this._buffers = {};

    // Load state per key: 'idle' | 'loading' | 'ready'
    this._loadState = {};

    // Loading queue: array of { song, bass, mix, priority }
    this._queue = [];
    this._activeLoads = 0;
    this._maxConcurrent = 4;

    // Event listeners
    this._listeners = {};

    // Pending play: if we called play but buffer wasn't ready, remember it
    this._pendingPlay = false;

    // Flag: waiting for a specific key before resuming
    this._waitingForKey = null;

    this._mediaOwnerId = 'sa-compare-engine-' + (++compareOwnerSeq);

    if (window.SAMediaCoordinator) {
      var self = this;
      window.SAMediaCoordinator.register(this._mediaOwnerId, {
        pause: function () { self._pause(); }
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Event emitter
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype.on = function (event, fn) {
    if (!this._listeners[event]) {
      this._listeners[event] = [];
    }
    this._listeners[event].push(fn);
  };

  SACompareEngine.prototype.off = function (event, fn) {
    if (!this._listeners[event]) return;
    this._listeners[event] = this._listeners[event].filter(function (f) {
      return f !== fn;
    });
  };

  SACompareEngine.prototype._emit = function (event, data) {
    var listeners = this._listeners[event];
    if (!listeners) return;
    for (var i = 0; i < listeners.length; i++) {
      try {
        listeners[i](data);
      } catch (e) {
        // swallow listener errors
      }
    }
  };

  // ---------------------------------------------------------------------------
  // URL / key helpers
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype._bufferKey = function (song, bass, mix) {
    return song + '-' + bass + '-' + mix;
  };

  SACompareEngine.prototype._buildUrl = function (song, bass, mix) {
    return this._urlPattern
      .replace('{song}', song)
      .replace('{bass}', bass)
      .replace('{mix}', mix);
  };

  // ---------------------------------------------------------------------------
  // AudioContext bootstrap
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype._ensureContext = function () {
    if (this._ctx) return;
    // Share with any existing player on the page
    if (window._saSharedAudioCtx) {
      this._ctx = window._saSharedAudioCtx;
    } else {
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
      window._saSharedAudioCtx = this._ctx;
    }
  };

  SACompareEngine.prototype._resumeContext = function () {
    if (this._ctx && this._ctx.state === 'suspended') {
      return this._ctx.resume();
    }
    return Promise.resolve();
  };

  // ---------------------------------------------------------------------------
  // Position helpers
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype._currentPosition = function () {
    if (!this._playing || !this._ctx) return this._startOffset;
    var elapsed = this._ctx.currentTime - this._startTime;
    var key = this._bufferKey(this._song, this._bass, this._mix);
    var buf = this._buffers[key];
    if (!buf) return this._startOffset;
    var pos = this._startOffset + elapsed;
    // Wrap within buffer duration
    return pos % buf.duration;
  };

  SACompareEngine.prototype._savePosition = function () {
    if (this._song !== null) {
      this._songPositions[this._song] = this._currentPosition();
    }
  };

  // ---------------------------------------------------------------------------
  // Buffer source management
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype._stopSource = function () {
    if (this._source) {
      try {
        this._source.onended = null;
        this._source.stop();
      } catch (e) {
        // already stopped
      }
      try { this._source.disconnect(); } catch (e) {}
      this._source = null;
    }
  };

  SACompareEngine.prototype._startSource = function (buffer, offset) {
    if (window.SAMediaCoordinator) {
      window.SAMediaCoordinator.claim(this._mediaOwnerId);
    }
    this._stopSource();
    if (!this._ctx || !buffer) return;

    var self = this;
    var src = this._ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.connect(this._ctx.destination);

    var safeOffset = (offset || 0) % buffer.duration;
    src.start(0, safeOffset);

    this._source = src;
    this._startTime   = this._ctx.currentTime;
    this._startOffset = safeOffset;
    this._playing = true;

    // Kick off timeupdate ticks
    this._scheduleTimeUpdate();
  };

  // ---------------------------------------------------------------------------
  // timeupdate ticker
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype._scheduleTimeUpdate = function () {
    var self = this;
    if (this._timeUpdateRaf) return; // already scheduled
    function tick() {
      if (!self._playing) {
        self._timeUpdateRaf = null;
        return;
      }
      self._emit('timeupdate', self.getState());
      self._timeUpdateRaf = requestAnimationFrame(tick);
    }
    this._timeUpdateRaf = requestAnimationFrame(tick);
  };

  // ---------------------------------------------------------------------------
  // Public playback controls
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype.togglePlayback = function () {
    if (this._playing) {
      this._pause();
    } else {
      this._play();
    }
  };

  SACompareEngine.prototype._play = function () {
    var self = this;
    var key = this._bufferKey(this._song, this._bass, this._mix);
    var buf = this._buffers[key];

    /* iOS silent switch bypass — see sa-demo-player.js for full history.
       Replaces the old silent MP3 looping hack with the Audio Session API. */
    if (navigator.audioSession) navigator.audioSession.type = 'playback';
    this._ensureContext();
    this._resumeContext().then(function () {
      if (buf) {
        var pos = self._startOffset; // resume from last saved offset
        self._startSource(buf, pos);
        self._emit('statechange', self.getState());
      } else {
        // Buffer not ready — mark pending, load with top priority
        self._pendingPlay = true;
        self._waitingForKey = key;
        self._enqueue(self._song, self._bass, self._mix, 10);
        self._processQueue();
        self._emit('statechange', self.getState());
      }
    });
  };

  SACompareEngine.prototype._pause = function () {
    this._startOffset = this._currentPosition();
    this._stopSource();
    this._playing = false;
    this._pendingPlay = false;
    this._waitingForKey = null;
    if (window.SAMediaCoordinator) {
      window.SAMediaCoordinator.release(this._mediaOwnerId);
    }
    this._emit('statechange', this.getState());
  };

  // ---------------------------------------------------------------------------
  // Public switching API
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype.setBass = function (bassId) {
    if (bassId === this._bass) return;
    var pos = this._currentPosition();
    this._stopSource();
    this._bass = bassId;
    this._startOffset = pos;
    this._reprioritise();
    this._tryResume();
  };

  SACompareEngine.prototype.setMix = function (mixId) {
    if (mixId === this._mix) return;
    var pos = this._currentPosition();
    this._stopSource();
    this._mix = mixId;
    this._startOffset = pos;
    this._reprioritise();
    this._tryResume();
  };

  SACompareEngine.prototype.setSong = function (songId) {
    if (songId === this._song) return;
    // Save position for outgoing song
    this._savePosition();
    this._stopSource();
    this._song = songId;
    // Restore saved position for this song, or start from beginning
    this._startOffset = this._songPositions[songId] !== undefined
      ? this._songPositions[songId]
      : 0;
    this._reprioritise();
    this._tryResume();
  };

  // Internal: attempt to resume playback after a switch
  SACompareEngine.prototype._tryResume = function () {
    var self = this;
    var key = this._bufferKey(this._song, this._bass, this._mix);
    var buf = this._buffers[key];
    var wasPlaying = this._pendingPlay || this._playing;

    this._playing = false;

    if (!wasPlaying) {
      this._emit('statechange', this.getState());
      return;
    }

    if (buf) {
      this._ensureContext();
      this._resumeContext().then(function () {
        self._startSource(buf, self._startOffset);
        self._emit('statechange', self.getState());
      });
    } else {
      // Wait for it
      this._pendingPlay = true;
      this._waitingForKey = key;
      this._emit('statechange', this.getState());
    }
  };

  // ---------------------------------------------------------------------------
  // start() — called on modal open
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype.init = function (songId, mixId, bassId) {
    this._ensureContext();
    this._song = songId;
    this._mix = mixId;
    this._bass = bassId;
    this._startOffset = this._songPositions[songId] !== undefined
      ? this._songPositions[songId] : 0;
    this._cascadeForSong(songId, mixId, bassId);
    this._processQueue();
    this._emit('statechange', this.getState());
  };

  SACompareEngine.prototype.start = function (songId, mixId, bassId) {
    this._ensureContext();

    this._song = songId;
    this._mix  = mixId;
    this._bass = bassId;
    this._startOffset = this._songPositions[songId] !== undefined
      ? this._songPositions[songId]
      : 0;

    this._pendingPlay = true;

    // Start cascade loading; play when the primary key is ready
    var key = this._bufferKey(songId, bassId, mixId);
    this._waitingForKey = key;

    this._cascadeForSong(songId, mixId, bassId);
    this._processQueue();

    this._emit('statechange', this.getState());
  };

  // ---------------------------------------------------------------------------
  // getState()
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype.getState = function () {
    var key = this._bufferKey(this._song, this._bass, this._mix);
    var buf = this._buffers[key];
    return {
      song:      this._song,
      mix:       this._mix,
      bass:      this._bass,
      playing:   this._playing,
      position:  this._currentPosition(),
      duration:  buf ? buf.duration : 0,
      loadState: Object.assign({}, this._loadState)
    };
  };

  // ---------------------------------------------------------------------------
  // destroy()
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype.destroy = function () {
    this._stopSource();
    this._queue = [];
    this._listeners = {};
    this._pendingPlay = false;
    this._waitingForKey = null;
    if (window.SAMediaCoordinator) {
      window.SAMediaCoordinator.release(this._mediaOwnerId);
    }
    if (this._timeUpdateRaf) {
      cancelAnimationFrame(this._timeUpdateRaf);
      this._timeUpdateRaf = null;
    }
    // Do NOT close the shared AudioContext
  };

  // ---------------------------------------------------------------------------
  // Loading queue
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype._enqueue = function (song, bass, mix, priority) {
    var key = this._bufferKey(song, bass, mix);

    // Already loaded, loading, or failed — skip
    var state = this._loadState[key];
    if (state === 'ready' || state === 'loading' || state === 'error') return;

    // Check if already in queue
    for (var i = 0; i < this._queue.length; i++) {
      if (this._queue[i].key === key) {
        // Update priority if higher
        if (priority > this._queue[i].priority) {
          this._queue[i].priority = priority;
          if (priority >= 10) {
            // Move to front
            var item = this._queue.splice(i, 1)[0];
            this._queue.unshift(item);
          }
        }
        return;
      }
    }

    var entry = { song: song, bass: bass, mix: mix, key: key, priority: priority };

    if (priority >= 10) {
      // Highest priority — goes to the very front
      this._queue.unshift(entry);
    } else {
      // Insert sorted descending by priority
      var inserted = false;
      for (var j = 0; j < this._queue.length; j++) {
        if (priority > this._queue[j].priority) {
          this._queue.splice(j, 0, entry);
          inserted = true;
          break;
        }
      }
      if (!inserted) {
        this._queue.push(entry);
      }
    }
  };

  SACompareEngine.prototype._processQueue = function () {
    var self = this;
    while (this._activeLoads < this._maxConcurrent && this._queue.length > 0) {
      var entry = this._queue.shift();
      var key = entry.key;

      // Double-check state (may have been loaded by another path)
      if (this._loadState[key] === 'ready' || this._loadState[key] === 'loading') {
        continue;
      }

      this._loadState[key] = 'loading';
      this._activeLoads++;
      this._emit('statechange', this.getState());

      (function (e) {
        var url = self._buildUrl(e.song, e.bass, e.mix);
        self._fetchBuffer(url)
          .then(function (buffer) {
            self._buffers[e.key] = buffer;
            self._loadState[e.key] = 'ready';
            self._activeLoads--;
            self._onBufferReady(e.key);
            self._processQueue();
          })
          .catch(function (err) {
            console.warn('[SACompareEngine] Failed to load:', url, err);
            self._loadState[e.key] = 'error';
            self._activeLoads--;
            self._emit('statechange', self.getState());
            self._processQueue();
          });
      })(entry);
    }
  };

  SACompareEngine.prototype._fetchBuffer = function (url) {
    var self = this;
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.arrayBuffer();
      })
      .then(function (ab) {
        return new Promise(function (resolve, reject) {
          self._ctx.decodeAudioData(ab, resolve, reject);
        });
      });
  };

  // Called when a buffer finishes loading
  SACompareEngine.prototype._onBufferReady = function (key) {
    this._emit('statechange', this.getState());

    // If we were waiting for this key, resume playback
    if (this._pendingPlay && this._waitingForKey === key) {
      this._pendingPlay = false;
      this._waitingForKey = null;
      var self = this;
      this._resumeContext().then(function () {
        self._startSource(self._buffers[key], self._startOffset);
        self._emit('statechange', self.getState());
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Cascade loading strategy
  // ---------------------------------------------------------------------------

  SACompareEngine.prototype._cascadeForSong = function (song, mix, bass) {
    var self = this;

    // Priority 10: current selection
    this._enqueue(song, bass, mix, 10);

    // Priority 5: other basses, same song × mix
    this._basses.forEach(function (b) {
      if (b.id !== bass) {
        self._enqueue(song, b.id, mix, 5);
      }
    });

    // Priority 1: other mixes, all basses for same song
    this._mixes.forEach(function (m) {
      if (m.id !== mix) {
        self._basses.forEach(function (b) {
          self._enqueue(song, b.id, m.id, 1);
        });
      }
    });
  };

  SACompareEngine.prototype._reprioritise = function () {
    // Clear pending items (but NOT in-flight — they will cache when done)
    // We only clear items that are not currently loading
    var self = this;
    var newQueue = [];
    // Drop everything from queue (in-flight loads continue, will cache)
    this._queue = [];

    // Rebuild cascade for current context
    this._cascadeForSong(this._song, this._mix, this._bass);
    this._processQueue();
  };

  // ---------------------------------------------------------------------------
  // Export
  // ---------------------------------------------------------------------------

  window.SACompareEngine = SACompareEngine;

})();
